import { useTableController } from "@/components/datatable/controller"
import { DataTable } from "@/components/datatable/datatable"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { ToolbarItem } from "@/components/datatable/toolbaritem"
import { Page, PageTitle } from "@/components/page"
import { useDatabaseData } from "@/components/page/hooks"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { deleteTeam, deleteTeams } from "@/lib/teams"
import { Users, Plus, Trash2 } from "lucide-react"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { usePermissions } from "@/lib/hooks/usePermissions"

export const teams = {
  title: "Teams",
  description: "Manage your organization's teams.",
  route: "/settings/teams",
  url: "/settings/teams",
  icon: Users,
  component,
  group: "settings",
  resource: "teams",
  action: "read",
} satisfies PageConfig

function component() {
  const navigate = useNavigate()
  const { data: users } = useDatabaseData("users")
  const ctrl = useTableController()
  const [selectedItems, setSelectedItems] = useState<Table<"teams">[]>([])
  const { canCreate, canDelete } = usePermissions()

  const userMap = useMemo(() => {
    const map = new Map<string, string>()
    users.forEach((user) => {
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || user.id
      map.set(user.id, name)
    })
    return map
  }, [users])

  const columns: Columns<Table<"teams">> = useMemo(() => ({
    name: { header: "Name", size: 300 },
    description: { header: "Description", size: 650 },
    owner: {
      header: "Owner",
      size: 200,
      cell: ({ row }) => userMap.get(row.original.owner) || row.original.owner || "-"
    },
    technical_owner: {
      header: "Technical Support",
      size: 200,
      cell: ({ row }) => userMap.get(row.original.technical_owner) || row.original.technical_owner || "-"
    },
    status: { header: "Status", size: 120 },
    created_at: { header: "Created", cell: DataTable.cellRender.date, size: 166 },
  }), [userMap])

  const menu: DropdownMenuContents = [
    ...(canDelete("teams") ? [{
      title: "Delete", action: async (v) => {
        await deleteTeam(v.id)
      },
      icon: <Trash2 />,
      ask: "Are you sure you want to delete this team? This will remove all related data (agents, collections, sources, users, etc.).",
    }] : []),
  ]

  async function onDeleteSelected() {
    const selected = ctrl.getSeleted()
    if (selected.length === 0) return
    ctrl.setLoading(true)
    await deleteTeams(selected.map((item) => item.id))
    ctrl.setLoading(false)
    ctrl.reload()
  }

  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        {canCreate("teams") && <div>
          <Button className="rounded-lg" onClick={() => navigate('/settings/teams/new')}><Plus className="h-4 w-4" />New Team</Button>
        </div>}
      </div>
      <DataTableSupabase
        table="teams"
        columns={columns}
        showSelection
        onRowClick={"/settings/teams"}
        sort={["created_at", "desc"]}
        actions={menu.length > 0 ? menu : undefined}
        enableServerSideFiltering={true}
        searchableColumns={["name", "description"]}
        controller={ctrl}
        onSelectedItemsChange={setSelectedItems}
      >
        {canDelete("teams") && selectedItems.length > 0 &&
          <ToolbarItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Trash2 size={16} className="cursor-pointer hover:text-red-500" />
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {selectedItems.length} team{selectedItems.length > 1 ? "s" : ""}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete the selected teams and all their related data (agents, collections, sources, users, etc.).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDeleteSelected}>Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </ToolbarItem>}
      </DataTableSupabase>
    </div>
  </Page>

}
